import {
  ChevronDown,
  Download,
  FileSpreadsheet,
  FileText,
  Info,
  Mail,
} from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DEMO_TRANSACTIONS_LEGACY,
  generateForecastData,
} from '@/data/demo-data';
import { useBaseForecast } from '@/hooks/useBaseForecast';
import { useTerminology } from '@/hooks/useTerminology';
import { getCashMovementTerms } from '@/lib/terminology-config';
import type { ForecastDataPoint } from '@/types';
import { ActionsDiscussionAuditPreview } from './previews/ActionsDiscussionAuditPreview';
import { ActionsWorkflowAuditPreview } from './previews/ActionsWorkflowAuditPreview';
import { CashFlowStatementPreview } from './previews/CashFlowStatementPreview';
import { DailyCashPositionPreview } from './previews/DailyCashPositionPreview';
import { DataSourceSyncReportPreview } from './previews/DataSourceSyncReportPreview';
import { ExecutiveSummaryPreview } from './previews/ExecutiveSummaryPreview';
import { ForecastSummaryPreview } from './previews/ForecastSummaryPreview';
import { LiquidityAnalysisPreview } from './previews/LiquidityAnalysisPreview';
import { RolesPermissionsAuditPreview } from './previews/RolesPermissionsAuditPreview';
import { ScenarioComparisonPreview } from './previews/ScenarioComparisonPreview';
import { ScenarioDiscussionAuditPreview } from './previews/ScenarioDiscussionAuditPreview';
import { ScenarioWorkflowAuditPreview } from './previews/ScenarioWorkflowAuditPreview';
import { SeasonalTrendsPreview } from './previews/SeasonalTrendsPreview';
import { SystemActivityLogPreview } from './previews/SystemActivityLogPreview';
import { VarianceAnalysisPreview } from './previews/VarianceAnalysisPreview';

export interface ReportDefinition {
  id: string;
  name: string;
  description: string;
  category: string;
  frequency?: string;
  formats: string[];
  schedulable?: boolean;
  comingSoon?: boolean;
}

interface Props {
  report: ReportDefinition | null;
  open: boolean;
  onClose: () => void;
}

// ─── CSV export helpers ────────────────────────────────────────────────────

function downloadCsv(filename: string, rows: string[][]): void {
  const csv = rows
    .map((row) =>
      row
        .map((cell) =>
          cell.includes(',') || cell.includes('"')
            ? `"${cell.replace(/"/g, '""')}"`
            : cell,
        )
        .join(','),
    )
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function exportCashFlowCsv(baseForecast: ForecastDataPoint[]): void {
  const rows: string[][] = [
    ['Date', `${getCashMovementTerms().in} (£)`, `${getCashMovementTerms().out} (£)`, 'Net (£)', 'Closing Balance (£)'],
    ...baseForecast
      .filter((_, i) => i % 7 === 0 || i === 90)
      .map((d) => [
        d.date.toLocaleDateString('en-GB'),
        d.incoming.toFixed(2),
        d.outgoing.toFixed(2),
        (d.incoming - d.outgoing).toFixed(2),
        d.balance.toFixed(2),
      ]),
  ];
  downloadCsv('buildright-cash-flow-statement.csv', rows);
}

function exportDailyCashCsv(baseForecast: ForecastDataPoint[]): void {
  const rows: string[][] = [
    ['Date', `${getCashMovementTerms().in} (£)`, `${getCashMovementTerms().out} (£)`, 'Closing Balance (£)'],
    ...baseForecast
      .slice(0, 91)
      .map((d) => [
        d.date.toLocaleDateString('en-GB'),
        d.incoming.toFixed(2),
        d.outgoing.toFixed(2),
        d.balance.toFixed(2),
      ]),
  ];
  downloadCsv('buildright-daily-cash-position.csv', rows);
}

function exportForecastCsv(baseForecast: ForecastDataPoint[]): void {
  const base = baseForecast;
  const conservative = generateForecastData('conservative');
  const optimistic = generateForecastData('optimistic');
  const rows: string[][] = [
    [
      'Date',
      'Base Balance (£)',
      'Conservative Balance (£)',
      'Optimistic Balance (£)',
    ],
    ...base
      .filter((_, i) => i % 7 === 0 || i === 90)
      .map((d, wi) => {
        const idx = wi * 7 > 90 ? 90 : wi * 7;
        return [
          d.date.toLocaleDateString('en-GB'),
          base[idx].balance.toFixed(2),
          conservative[idx].balance.toFixed(2),
          optimistic[idx].balance.toFixed(2),
        ];
      }),
  ];
  downloadCsv('buildright-forecast-summary.csv', rows);
}

function exportTransactionsCsv(): void {
  const rows: string[][] = [
    ['Date', 'Source', 'Reference', 'Description', 'Transaction Type', 'Category', 'Amount (£)', 'Status'],
    ...DEMO_TRANSACTIONS_LEGACY.map((t) => [
      t.date.toLocaleDateString('en-GB'),
      t.source ?? '',
      t.reference ?? '',
      t.description,
      t.transactionType ?? '',
      t.category,
      t.amount.toFixed(2),
      t.status,
    ]),
  ];
  downloadCsv('buildright-transactions.csv', rows);
}

// ─── Report preview registry ───────────────────────────────────────────────

function PreviewContent({ reportId }: { reportId: string }) {
  switch (reportId) {
    case 'cash-flow-statement':
      return <CashFlowStatementPreview />;
    case 'forecast-summary':
      return <ForecastSummaryPreview />;
    case 'variance-analysis':
      return <VarianceAnalysisPreview />;
    case 'cash-position':
      return <DailyCashPositionPreview />;
    case 'scenario-comparison':
      return <ScenarioComparisonPreview />;
    case 'executive-summary':
      return <ExecutiveSummaryPreview />;
    case 'liquidity-analysis':
      return <LiquidityAnalysisPreview />;
    case 'seasonal-trends':
      return <SeasonalTrendsPreview />;
    case 'roles-permissions-audit':
      return <RolesPermissionsAuditPreview />;
    case 'data-source-sync-report':
      return <DataSourceSyncReportPreview />;
    case 'system-activity-log':
      return <SystemActivityLogPreview />;
    case 'scenario-workflow-audit':
      return <ScenarioWorkflowAuditPreview />;
    case 'scenario-discussion-audit':
      return <ScenarioDiscussionAuditPreview />;
    case 'actions-workflow-audit':
      return <ActionsWorkflowAuditPreview />;
    case 'actions-discussion-audit':
      return <ActionsDiscussionAuditPreview />;
    default:
      return (
        <div className="flex flex-col items-center justify-center py-20 text-center text-muted-foreground gap-3">
          <FileText className="h-10 w-10 opacity-40" />
          <p className="text-sm">
            Preview not yet available for this report type.
          </p>
        </div>
      );
  }
}

// Reports that support real CSV export
const CSV_SUPPORTED = new Set([
  'cash-flow-statement',
  'forecast-summary',
  'cash-position',
  'scenario-comparison',
]);

function handleExport(
  reportId: string,
  format: string,
  baseForecast: ForecastDataPoint[],
): void {
  if (format === 'CSV' && CSV_SUPPORTED.has(reportId)) {
    switch (reportId) {
      case 'cash-flow-statement':
        exportCashFlowCsv(baseForecast);
        toast.success('CSV exported', {
          description: 'buildright-cash-flow-statement.csv downloaded.',
        });
        break;
      case 'forecast-summary':
        exportForecastCsv(baseForecast);
        toast.success('CSV exported', {
          description: 'buildright-forecast-summary.csv downloaded.',
        });
        break;
      case 'cash-position':
        exportDailyCashCsv(baseForecast);
        toast.success('CSV exported', {
          description: 'buildright-daily-cash-position.csv downloaded.',
        });
        break;
      case 'scenario-comparison':
        exportForecastCsv(baseForecast);
        toast.success('CSV exported', {
          description: 'buildright-forecast-summary.csv downloaded.',
        });
        break;
    }
    return;
  }

  // All other formats — believable toast
  const formatLabels: Record<string, string> = {
    PDF: 'PDF document',
    Excel: 'Excel workbook',
    Email: 'email delivery',
  };
  toast.success(`Exporting as ${format}`, {
    description: `Your ${formatLabels[format] ?? format} is being prepared and will download shortly.`,
    duration: 4000,
  });
}

// ─── Modal ─────────────────────────────────────────────────────────────────

export const ReportPreviewModal: React.FC<Props> = ({
  report,
  open,
  onClose,
}) => {
  const { getCashMovementTerms, getForecastTerm, getVarianceTerm } = useTerminology();
  const { baseForecast } = useBaseForecast();
  if (!report) return null;

  const dateLabel = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const categoryLabels: Record<string, string> = {
    financial: 'Financial',
    forecast: getForecastTerm(true),
    variance: `${getVarianceTerm(true)} Analysis`,
    admin: 'Administration',
    custom: 'Custom',
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-5xl w-full h-[90vh] flex flex-col p-0 gap-0">
        {/* Fixed header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <DialogTitle className="text-xl">{report.name}</DialogTitle>
                <Badge variant="secondary" className="capitalize text-xs">
                  {categoryLabels[report.category] ?? report.category}
                </Badge>
                {report.frequency && (
                  <Badge variant="outline" className="capitalize text-xs">
                    {report.frequency}
                  </Badge>
                )}
              </div>
              <DialogDescription className="text-sm">
                {report.description} · Generated {dateLabel}
              </DialogDescription>
            </div>

            {/* Export dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="default"
                  size="sm"
                  className="flex-shrink-0 gap-1.5"
                >
                  <Download className="h-4 w-4" />
                  Export
                  <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                  Choose format
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {report.formats.map((fmt) => {
                  const isReal = fmt === 'CSV' && CSV_SUPPORTED.has(report.id);
                  return (
                    <DropdownMenuItem
                      key={fmt}
                      onClick={() => handleExport(report.id, fmt, baseForecast)}
                      className="gap-2"
                    >
                      {fmt === 'PDF' ? (
                        <FileText className="h-4 w-4 text-muted-foreground" />
                      ) : fmt === 'Email' ? (
                        <Mail className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span>{fmt}</span>
                      {isReal && (
                        <span className="ml-auto text-xs text-green-600 font-medium">
                          Live
                        </span>
                      )}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </DialogHeader>

        {/* Scrollable report body */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="px-6 py-5">
            <PreviewContent reportId={report.id} />
          </div>
        </ScrollArea>

        {/* Fixed footer */}
        <div className="px-6 py-3 border-t flex-shrink-0 flex items-center justify-between gap-4 bg-muted/30">
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>
              This is a preview using live demo data. Exported files will
              include full data and company branding. Formats marked{' '}
              <span className="text-green-600 font-medium">Live</span> produce
              real downloadable files.
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="flex-shrink-0"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
